// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@stanion/App/Components/Common/XngZone";
import { xScreen } from "@stanion/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

export interface MyAccountDetailsPagesLayout {
    container?: InjectableCss;
    titleContainer?: InjectableCss;
    extraWidgetContainer?: InjectableCss;
    informationContainer?: InjectableCss;
    productLisContainer?: InjectableCss;
    information?: InjectableCss;
    total?: InjectableCss;
    totalEmpty?: InjectableCss;
    productList?: InjectableCss;
}

export const myAccountDetailsPagesLayout: MyAccountDetailsPagesLayout = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 24px;
            @media print {
                display: block;
            }
        `,
    },
    extraWidgetContainer: {
        css: css`
            &:empty,
            &:has(> div:empty) {
                display: none;
            }
        `,
    },
    informationContainer: {
        css: css`
            display: flex;
            gap: 40px;
            ${xScreen("lte", "md")} {
                gap: 16px;
            }
            ${xScreen("lte", "xs")} {
                row-gap: 20px;
                flex-direction: column;
            }
            @media print {
                display: block;
            }
        `,
    },
    information: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            ${xScreen("lte", "xs")} {
                flex: 1 1 100%;
                width: 100%;
            }
        `,
    },
    productList: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            ${xScreen("lte", "md")} {
                flex: 1 1 100%;
                width: 100%;
            }
        `,
    },
    total: {
        css: css`
            flex: 0 0 319px;
            ${xScreen("lte", "md")} {
                flex-basis: 234px;
            }
            ${xScreen("lte", "xs")} {
                flex-basis: 100%;
            }
        `,
    },
    totalEmpty: {
        css: css`
            flex: 0 0 319px;
            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    productLisContainer: {
        css: css`
            &:empty,
            &:has(> div:empty) {
                display: none;
            }
        `,
    },
};

const styles = myAccountDetailsPagesLayout;
const MyAccountDetailsPagesLayout: FC<WidgetProps> = ({ id }) => {
    return (
        <StyledWrapper {...styles.container} data-test-selector="My-Account-Details-Pages-Layout">
            <StyledWrapper {...styles.titleContainer}>
                <XngZone contentId={id} zoneName="Content01" hint="Zone For Title" />
            </StyledWrapper>
            <StyledWrapper {...styles.extraWidgetContainer}>
                <XngZone contentId={id} zoneName="Content02" hint="Zone For Extra Widgets" />
            </StyledWrapper>
            <StyledWrapper {...styles.informationContainer}>
                <StyledWrapper {...styles.information}>
                    <XngZone contentId={id} zoneName="Content03" hint="Zone For Information" />
                </StyledWrapper>
                <StyledWrapper {...styles.total}>
                    <XngZone contentId={id} zoneName="Content04" hint="Zone For Total" />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.informationContainer}>
                <StyledWrapper {...styles.productList}>
                    <XngZone contentId={id} zoneName="Content06" hint="Zone Product List Not Full Width" />
                </StyledWrapper>
                <StyledWrapper {...styles.totalEmpty}></StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.productLisContainer}>
                <XngZone contentId={id} zoneName="Content05" hint="Zone Product List" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: MyAccountDetailsPagesLayout,
    definition: {
        group: "Layout" as any,
        displayName: "My Account Details Pages Layout",
    },
};

export default widgetModule;
